import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import "./ChatBot.css";

export default function ChatBotMultiCase({ uploadedBatch, onAnalysisDone }) {
  const [msg, setMsg] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);

  const [pendingAnalysis, setPendingAnalysis] = useState(null); 
  // pendingAnalysis: { files, mode, uploadedAt }

  const chatEndRef = useRef(null);

  const pushMsg = (m) => setMsg((prev) => [...prev, m]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [msg, loading]);

  useEffect(() => {
    if (!uploadedBatch?.savedFiles?.length) return;

    setPendingAnalysis(uploadedBatch);

    pushMsg({
      role: "bot",
      type: "confirm",
      text: `Detectes ${uploadedBatch.savedFiles.length} PDF files uploaded. Do you want to start analysis?`,
      actions: [
        { key: "start", label: "Yes" },
        { key: "cancel", label: "Upload more" },
      ],
    });
  }, [uploadedBatch?.uploadedAt]); 
  // 用 uploadedAt 當觸發鍵，避免同一批被重複 useEffect

  const sendMsg = async () => {
    if (!input.trim() || loading) return;

    // 還在等你按「開始分析」的話，先提醒
    if (pendingAnalysis) {
      pushMsg({
        role: "bot",
        type: "info",
        text: "我已收到你上傳的檔案～要先按「開始分析」或「先不要」喔。",
      });
      return;
    }

    const userText = input;
    const newMsg = [...msg, { role: "user", text: userText }];
    setMsg(newMsg);
    setInput("");
    setLoading(true);

    try {
      const res = await axios.post("/api/multicaseanalysis", {
        page: "multicase",
      });

      const results = res.data?.results || [];

      onAnalysisDone?.(results);

      pushMsg({ role: "bot", type: "info", text: `Analysis finished：${results.length} data showed on the right screen` });
    } catch (err) {
      console.error(err);
      pushMsg({ role: "bot", type: "error", text: "Somwthing failed" });
    } finally {
      setLoading(false);
    }
  };

  const startAnalysis = async () => {
    if (!pendingAnalysis || loading) return;

    const { tag, savedFiles } = pendingAnalysis;
    setPendingAnalysis(null);
    setLoading(true);

    pushMsg({ role: "bot", type: "info", text: "Starting analysis..." });

    try {
    const res = await axios.post("/api/multicaseanalysis", {
      page: "patho",
      type: tag,
      files: savedFiles,
    });

    const results = res.data?.results || [];
    onAnalysisDone?.(results);
    console.log("results length:", results.length, results);

    pushMsg({
      role: "bot",
      type: "info",
      text: `Analysis finished：${results.length} data showed on the right screen`,
    });
  } catch (err) {
    console.error(err);
    pushMsg({ role: "bot", type: "error", text: "分析失敗，請檢查 /api/multicaseanalysis。" });
  } finally {
    setLoading(false);
  }
  };

  const cancelAnalysis = () => {
    setPendingAnalysis(null);
    pushMsg({ role: "bot", type: "info", text: "Do you want to ask any questions about this system?" });
  };

  const onConfirmAction = (key) => {
    if (key === "start") startAnalysis();
    if (key === "cancel") cancelAnalysis();
  };

  return (
    <div className="chatbot-container">
      <div className="chatbot-header">
        <h3>ChatBot</h3>
      </div>

      <div className="chat-window">
        {msg.map((m, i) => (
          <div key={i} className={`message ${m.role}`}>
            <div className={`bubble ${m.type || ""}`}>
              {m.text}

              {m.type === "confirm" && (
                <div className="bubble-actions">
                  {m.actions.map((a) => (
                    <button
                      key={a.key}
                      className={`action-btn ${a.key === "start" ? "primary" : "ghost"}`}
                      onClick={() => onConfirmAction(a.key)}
                      disabled={loading}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {loading && <div className="loading">Bot is typing...</div>}
        <div ref={chatEndRef} />
      </div>

      <div className="chat-input">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              sendMsg();
            }
          }}
          placeholder={pendingAnalysis ? "Please start the analysis after uploading" : "Type a message..."}
          disabled={loading}
        />
        <button onClick={sendMsg} disabled={loading}>
          Send
        </button>
      </div>
    </div>
  );
}
